import { debounce } from "lodash";
import * as ResizeSensor from "resize-sensor";
import { FlexGrid, HeadersVisibility } from "wijmo/wijmo.grid";

/**
 * The function make the grid responsive with adding horizontal scrollbar on the small screen sizes.
 * The function should be called in initilize function of the grid with the grid obejct.
 * @param grid FlexGrid object
 */
export const wijmoGridResponsive = (grid: FlexGrid) => {
	/**
	 * Saving the frozenColumns in the variable.
	 * To make the frozenColumns as 0 on the small screen.
	 */
	const frozenColumns = grid.frozenColumns;

	// A utility function to get the width of a grid.
	const getGridFullWidth = (grid: FlexGrid): number => {
		if (grid.headersVisibility === HeadersVisibility.All || grid.headersVisibility === HeadersVisibility.Row) {
			return grid.hostElement.offsetWidth - grid.rowHeaders.width;
		}
		return grid.hostElement.offsetWidth;
	};

	const wijmoGirdResponsiveHandler = (grid: FlexGrid, frozenColumns: number, timeout = 200) => {
		if (!grid.collectionView) {
			return null;
		}
		setTimeout(() => {
			/** Handling star sizing width */
			const starSizingColumn = grid.columns.find((col) => col.visible && col.width === "*");

			/** Executing autoSize function of Wijmo grid */
			grid.autoSizeColumns();

			const gridFullWidth = getGridFullWidth(grid);
			const columnsWidth = grid.columns.getTotalSize();

			if (starSizingColumn) {
				/** Making the starSized column back to start width */
				starSizingColumn!.width = "*";
			} else if (gridFullWidth > columnsWidth) {
				/** Distributing the blank space to each column equally */
				const columnCount = grid.columns.filter((column) => column.visible && !column.maxWidth).length;
				const extraWidthForEachColumn = (gridFullWidth - columnsWidth) / columnCount;
				grid.columns.forEach((column) => {
					const widthType = column.minWidth ? "minWidth" : "width";
					column[widthType] = column[widthType] + extraWidthForEachColumn;
				});
			}

			// Setting the frozenColumns as 0 on small screen
			if (gridFullWidth <= 768) {
				grid.frozenColumns = 0;
			} else {
				grid.frozenColumns = frozenColumns;
			}
		}, timeout);
	};

	// Registering the rezise listener to grid.
	const debouncedResponsiveHandler = debounce(() => {
		wijmoGirdResponsiveHandler(grid, frozenColumns);
	}, 300);
	new ResizeSensor(grid.hostElement, () => {
		debouncedResponsiveHandler();
	});

	// Calling the wijmoGirdResponsiveHandler while rendering the rows
	grid.loadedRows.addHandler((grid) => wijmoGirdResponsiveHandler(grid, frozenColumns, 500));

	// Calling the wijmoGirdResponsiveHandler while grid layout gets updated once
	let isResizeDone = false;
	grid.updatedLayout.addHandler((grid) => {
		if (!isResizeDone) {
			wijmoGirdResponsiveHandler(grid, frozenColumns, 500);
			isResizeDone = true;
		}
	});

	// Calling the wijmoGirdResponsiveHandler when a cell has been edited
	grid.cellEditEnded.addHandler((grid) => wijmoGirdResponsiveHandler(grid, frozenColumns, 0));
};
